import React, {useEffect, useState} from 'react';
import {Col, Container, Row} from 'reactstrap';
import {DateTime} from 'luxon';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import {usePhaseState} from 'funnel-schedule/phase-context';
import useBalanceText from 'utils/useBalanceText';

import PageMeta from 'components/PageMeta';
import {SectionTriangle, TriangleBottom} from 'components/Elements/Section';
import ShareButtons from 'components/ShareButtons';
import Laurels from 'components/Laurels';
import Video from 'components/Video';
import CountdownTimer from 'components/CountdownTimer';
import OptinModal, {OptinModalTrigger} from 'components/OptinModal';
import OptInForm from 'components/OptInForm';
import Footer from 'components/Footer';

//@ts-ignore
import orids from 'data/orids.json';

export default function IndexTrailer({isEvergreen = true, variant = 'default', ...props}) {
  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before',
        launchStart: '',
        launchEnd: ''
      },
      special: specialSchedule = {}
    }
  } = usePhaseState();
  const phase = specialSchedule.phase ? specialSchedule.phase : defaultSchedule.phase;
  const [partnerName, partnerLogo] = (specialSchedule.orid && orids[specialSchedule.orid]) || [];

  const toOrdinalSuffix = (num) => {
    const int = parseInt(num),
      digits = [int % 10, int % 100],
      ordinals = ['st', 'nd', 'rd', 'th'],
      oPattern = [1, 2, 3, 4],
      tPattern = [11, 12, 13, 14, 15, 16, 17, 18, 19];
    return oPattern.includes(digits[0]) && !tPattern.includes(digits[1])
      ? int + ordinals[digits[0] - 1]
      : int + ordinals[3];
  };

  const launchDate = (
    specialSchedule.endTime ? new Date(specialSchedule.endTime) : new Date(defaultSchedule.launchEnd)
  ).getTime();
  const launchStart = specialSchedule.startTime
    ? DateTime.fromMillis(specialSchedule.startTime)
    : DateTime.fromISO(defaultSchedule.launchStart);
  const launchStartNum = launchStart.setZone('America/Los_Angeles').toFormat('d');
  const launchEnd = specialSchedule.endTime
    ? DateTime.fromMillis(specialSchedule.endTime)
    : DateTime.fromISO(defaultSchedule.launchEnd);
  const launchEndNum = launchEnd.setZone('America/Los_Angeles').toFormat('d');

  const timerHeadline = defaultSchedule.timerHeadline ? defaultSchedule.timerHeadline : 'Your free viewing expires in:';
  const screeningEntry = 'default';

  //useBalanceText();
  return (
    <div className="page d-flex flex-column">
      <PageMeta />
      <div className="page-content">
        <SectionTriangle
          id="tntg-landing-header"
          color="black"
          className="section-triangle-first"
          imgUrl="https://cdn.foodrevolution.org/tntg/images/tntg-landing-001.jpg"
          offset="bottom"
          innerClass="p-0"
        >
          <div className="background-black-80 text-white text-center pt-4 pb-3" style={{position: 'relative'}}>
            <h5 className="section-only-heading">Now Playing FREE for a Limited Time!</h5>
            {phase === 'during' && (
              <div className="mt-4">
                <CountdownTimer time={launchDate} className="countdown-small" />
              </div>
            )}
            <TriangleBottom color="black-80" />
          </div>
          <Container>
            <Row>
              <Col>
                <div className="optin-box background-light-gray rounded box-shadow-black-50 p-4 p-lg-5">
                  <h4 className="text-center text-din text-400 mb-2">Enter Your Email Now to Watch</h4>
                  <h2 className="text-h1 text-condensed text-center mt-0 mb-4">
                    <i>The Need to GROW</i>
                  </h2>
                  <OptInForm linkColor="black" id="landingOptInForm" isEvergreen={isEvergreen} entry={screeningEntry} />
                </div>
              </Col>
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle id="tntg-landing-video" color="white" type="both" className="text-center">
          <Container>
            <Row>
              <Col>
                <Laurels noTomato />
              </Col>
            </Row>
            <Row className="mt-5">
              <Col>
                <h2 className="text-condensed mt-0">Finally. A film about environmental solutions!</h2>
                <h3 className="text-400">
                  This is a story of{' '}
                  <b>
                    CUTTING-EDGE <br className="d-none d-xl-block" />
                    SOLUTIONS
                  </b>{' '}
                  to climate change.
                </h3>
                <h5 className="text-400 mb-2">
                  With the planet on the brink of ecological disaster, <br className="d-none d-xl-block" />
                  with chronic disease rates skyrocketing, many are wondering...
                </h5>
                <h5 className="text-400">“What can I do?”</h5>
                <h4 className="mt-4 mb-2 text-400">
                  Start by watching <em>The Need To GROW.</em>
                </h4>
                <h4 className="text-400">An environmental film that gives the world hope.</h4>
                <h3 className="text-condensed text-800 mt-4 mb-5">
                  <i>Change happens when people know the truth.</i>
                </h3>
              </Col>
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle id="tntg-landing-quote" color="light-gray" type="bottom" className="text-center">
          <Container>
            <Row>
              <Col>
                <h3 className="text-condensed text-800 text-uppercase text-center mb-5">
                  The Environmental Film The World Has Been Waiting For
                </h3>
                <h5 className="mb-0">“No human being on the planet should miss this film.”</h5>
                <p>
                  <em>— Society of Voice Arts and Sciences</em>
                </p>
                <h5 className="mb-0">“Perhaps the best film on sustainability I have ever seen.”</h5>
                <p>
                  <em>— Teddy Grouya, Founding Director - American Documentary Film Festival</em>
                </p>
                <h5 className="mb-0">
                  “I loved this movie...it was one of those environmental movies that gave me hope.”
                </h5>
                <p className="mb-0">
                  <em>— Todd James, Global News</em>
                </p>
              </Col>
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle
          id="tntg-landing-laugh"
          color="black"
          imgUrl="https://cdn.foodrevolution.org/tntg/images/tntg-landing-002.jpg"
          offset="bottom"
          className="background-overlay-black-80 text-white text-shadow-black text-center"
        >
          <Container className="py-5">
            <Row>
              <Col>
                <h2 className="text-condensed text-800 m-0">
                  It will make you laugh, make you cry, <br className="d-none d-xl-block" />
                  give you chills, and inspire you to participate <br className="d-none d-xl-block" />
                  in the restoration of this beautiful Earth.
                </h2>
              </Col>
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle id="tntg-landing-innovation" color="white" type="both" className="text-center">
          <Container>
            <Row>
              <Col>
                <h4 className="text-condensed text-800 mb-3">
                  <em>The Need To GROW</em> takes you inside the hearts <br className="d-none d-xl-block" />
                  and innovations of three very different leaders.
                </h4>
                <p>An 8-year-old girl challenges the ethics of a global organization.</p>
                <p>A renegade farmer struggles to keep his land as he revolutionizes resource-efficient agriculture.</p>
                <p>A visionary inventor faces catastrophe in the midst of developing a game-changing technology.</p>
              </Col>
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle
          id="tntg-landing-cta"
          color="black"
          imgUrl="https://cdn.foodrevolution.org/tntg/images/tntg-landing-003.jpg"
          className="background-overlay-black-80 text-white text-shadow black text-center"
          offset="bottom"
        >
          <Container>
            <Row>
              <Col>
                <h4 className="text-400 mb-2">
                  <i>Don’t Miss Your Chance!</i>
                </h4>
                <h5 className="mb-4">For a limited time, watch</h5>
                <div>
                  <img
                    className="mb-5"
                    src="https://cdn.foodrevolution.org/tntg/tntg-logo-white.svg"
                    alt="The Need to GROW logo"
                    style={{maxHeight: '300px'}}
                  />
                </div>
                <OptinModalTrigger isEvergreen={isEvergreen} />
                {defaultSchedule.phase === 'during' && (
                  <>
                    <OptinModalTrigger />
                    <div className="section-countdown text-center mt-4">
                      <Row className="d-flex align-items-center justify-content-center m-0">
                        <Col xs="12">
                          <h3 className="text-condensed text-800 mb-0 mb-lg-3">{timerHeadline}</h3>
                        </Col>
                        <Col xs="12" className="timer">
                          <CountdownTimer time={launchDate} />
                        </Col>
                      </Row>
                    </div>
                  </>
                )}
              </Col>
            </Row>
          </Container>
        </SectionTriangle>

        <SectionTriangle id="" color="white" className="text-center" type="top">
          <Container>
            <Row>
              <Col>
                <h4 className="text-condensed text-800">
                  FREE Screening Of <em>The Need To GROW</em> <br className="d-none d-md-block d-lg-none" />
                  Brought To You By:
                </h4>
                <div className="logos mb-3">
                  <img
                    className="img-fluid logo-footer logo-ecf background-black rounded"
                    src={require('static/logo-ecf-new.png')}
                  />
                  <img
                    className="img-fluid logo-footer logo-frn"
                    src="https://cdn.foodrevolution.org/global/frn-logo-2024-trademarked-color.svg"
                  />
                </div>
              </Col>
            </Row>
          </Container>
        </SectionTriangle>
      </div>
      <div className="footer-content mt-auto">
        <Footer />
        <ShareButtons floating />
      </div>
      <OptinModal isEvergreen={isEvergreen} entry={screeningEntry} />
    </div>
  );
}
